"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const stages = [{
  numeral: "I",
  step: "01",
  name: "DISCOVERY",
  description: "We audit your existing operational bottlenecks, identifying high-friction manual tasks ripe for intelligent automation."
}, {
  numeral: "II",
  step: "02",
  name: "ARCHITECTURE",
  description: "Our engineers map custom workflows, integrating AI agents that bridge the gap between your data and your goals."
}, {
  numeral: "III",
  step: "03",
  name: "INTEGRATION",
  description: "We deploy secure, scalable automation layers that sync seamlessly with your current software stack and infrastructure."
}, {
  numeral: "IV",
  step: "04",
  name: "OPTIMIZATION",
  description: "Continuous monitoring and refinement ensure your systems evolve, maintaining peak efficiency as your business scales."
}];
function StageItem({
  stage,
  index
}: {
  stage: typeof stages[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3,
    rootMargin: "0px 0px -50px 0px"
  });
  return <div ref={ref} data-index={index} className="relative pl-12 sm:pl-16 transition-all duration-700 ease-out" style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(24px)",
    transitionDelay: `${index * 120}ms`
  }}>
      {}
      <span aria-hidden="true" className="absolute left-0 top-2 flex h-8 w-8 items-center justify-center -translate-x-1/2">
        <span className="block h-2 w-2 rounded-lg bg-foreground/70" />
        <span className="absolute inset-0 rounded-lg border border-foreground/30" />
      </span>

      <div className="flex items-baseline gap-4">
        <span className="font-default italic text-foreground/70 text-xl sm:text-2xl" style={{
        fontFamily: "var(--font-serif)"
      }}>
          {stage.numeral}
        </span>
        <span className="font-mono text-xs text-muted-foreground tracking-[0.3em]">
          {stage.step}
        </span>
        <span className="font-mono text-xs sm:text-sm text-foreground tracking-[0.3em] uppercase">
          {stage.name}
        </span>
      </div>

      <p className="mt-3 max-w-md text-sm sm:text-base text-muted-foreground leading-relaxed font-default">
        {stage.description}
      </p>
    </div>;
}
export default function Autonomy() {
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="4466" id="the-path-to-autonomy" className="bg-background text-foreground relative overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
        {}
        <div ref={imageRef} className="relative h-[60vh] sm:h-[75vh] lg:h-auto lg:min-h-[900px] overflow-hidden border-b lg:border-b-0 lg:border-r border-border/60">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_automation_infrastructure.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_automation_infrastructure.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ai_automation_infrastructure.webp-wvc-sizes" alt="Abstract digital neural network representing AI automation and data flow" loading="lazy" decoding="async" className="absolute inset-0 h-[115%] w-full object-cover will-change-transform transition-transform duration-[1400ms] ease-out" style={{
          transform: imageVisible ? "translateY(-4%) scale(1.02)" : "translateY(0) scale(1.06)"
        }} />
          {}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-background/40 via-transparent to-background/60" />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />

          {}
          <div className="absolute bottom-6 left-6 sm:bottom-10 sm:left-10 z-10">
            <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.35em] text-foreground/70">
              AutolexaAi — Systems Intelligence
            </p>
          </div>
        </div>

        {}
        <div className="relative flex items-center px-6 py-16 sm:px-12 sm:py-24 lg:px-16 xl:px-24 lg:py-32">
          <div className="w-full max-w-xl">
            {}
            <div ref={headingRef} className="transition-all duration-700 ease-out" style={{
            opacity: headingVisible ? 1 : 0,
            transform: headingVisible ? "translateY(0)" : "translateY(16px)"
          }}>
              <div className="flex items-center gap-4 mb-10">
                <span className="block h-px w-10 bg-foreground/70" />
                <span className="font-mono text-xs uppercase tracking-[0.4em] text-foreground/70">
                  04 — The Automation Blueprint
                </span>
              </div>

              {}
              <h2 className="font-default text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.02] tracking-tight text-foreground" style={{
              fontFamily: "var(--font-serif)"
            }}>
                Logic.{" "}
                <span className="block italic text-foreground/70">
                  The core
                </span>
                <span className="block">of your</span>
                <span className="block italic">efficiency.</span>
              </h2>

              <p className="mt-8 max-w-md text-base text-muted-foreground leading-relaxed font-default">
                Every workflow at AutolexaAi is engineered to eliminate friction, 
                transforming complex manual operations into seamless, autonomous 
                digital experiences.
              </p>
            </div>

            {}
            <div className="relative mt-16">
              {}
              <div aria-hidden="true" className="absolute left-4 top-2 bottom-2 w-px bg-gradient-to-b from-foreground/10 via-foreground/60 to-foreground/10" />

              <div className="flex flex-col gap-12">
                {stages.map((stage, i) => <StageItem key={stage.step} stage={stage} index={i} />)}
              </div>
            </div>

            {}
            <div className="mt-16 flex items-center gap-5">
              <span className="block h-px w-12 bg-foreground/50" />
              <p className="italic text-base sm:text-lg text-foreground/80" style={{
              fontFamily: "var(--font-serif)"
            }}>
                Precision-engineered automation for modern business.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}